import React, { useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../../../context/contextSelectors"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    fetchGuestsSummaryRatePerResource,
    ServiceCenterGuestsSummaryReportResponse,
    ServiceCenterStatisticsQuery,
} from "../../statisticsApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { GuestReportEntry } from "../../statisticsTypes"
import { useExportGuests } from "../../hooks/useExportGuests"
import { useDateTime } from "../../../../../localization/useDateTime"

const selectBookingSummaries = (data: ServiceCenterGuestsSummaryReportResponse) => data?.numberOfGuests || []

export function GuestsSummary() {
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)
    const { l10n } = useAppLocalization()
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()
    const tableInstanceRef = useRef<MRT_TableInstance<GuestReportEntry>>(null)

    const bookingSummariesResult = useApiQuery(fetchGuestsSummaryRatePerResource, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_GUESTS_SUMMARY,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        enabled: Boolean(serviceCenterId),
        select: selectBookingSummaries,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(bookingSummariesResult)

    const columns = useMemo<MRT_ColumnDef<GuestReportEntry>[]>(
        () => [
            columnFactory.dateColumn(
                {
                    header: l10n.getString("service-center-reports-statistic-guests-table-date-title"),
                    accessorKey: "date",
                },
                (row) => row.date,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("service-center-reports-statistic-guests-table-guests-title"),
                accessorKey: "number",
            }),
        ],
        [l10n, columnFactory],
    )

    const exportBookingDurations = useExportGuests({
        data: bookingSummariesResult.data,
        startDate: dateState.startDate || today,
        endDate: dateState.endDate || today,
    })

    return (
        <>
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                initialState={{ density: "spacious" }}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton
                            onClick={() => {
                                exportBookingDurations()
                            }}
                        />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <LocalizedStrict
                            id="service-center-reports-statistic-occupancy-table-date-range-filter"
                            attrs={{ label: true }}
                        >
                            <StandardDateRangeFilter
                                value={dateState}
                                onChange={setDateState}
                                label="Date range"
                                variant="labeled"
                            />
                        </LocalizedStrict>
                    </StandardTableFilter>
                )}
            />
        </>
    )
}
